import React, { useState } from "react";
import { Modal, Icon } from "./common";
import Avatar from "./Avatar";
import { useLang } from "../i18n";
import { useConfig } from "../context/ConfigContext";
import api, { apiError } from "../api";
import { toast } from "sonner";

export default function AwardBadgeModal({ open, onClose, students = [], presetId = null, onDone }) {
  const { t, pick } = useLang();
  const { config } = useConfig();
  const badges = config?.badges || [];
  const [studentId, setStudentId] = useState(presetId);
  const [badgeKey, setBadgeKey] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStudentId(presetId); setBadgeKey(null); setReason(""); };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/staff/badges/award", { student_id: studentId, badge_key: badgeKey, reason });
      toast.success(t("teacher.badge_awarded"));
      reset();
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title={t("teacher.award_badge")} testId="award-badge-modal">
      <div className="space-y-4">
        {!presetId && (
          <div>
            <p className="text-sm font-extrabold mb-2">{t("teacher.student_label")}</p>
            <div className="max-h-36 overflow-y-auto space-y-1.5">
              {students.map((s) => (
                <button key={s.id} data-testid={`badge-student-${s.username}`} onClick={() => setStudentId(s.id)}
                  className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 border transition-colors ${studentId === s.id ? "bg-[#7625D8]/25 border-[#9C42F5]/60" : "bg-[#0D0B38]/50 border-white/8 hover:border-white/20"}`}>
                  <Avatar src={s.avatar} name={pick(s, "name")} size="xs" />
                  <span className="text-sm font-bold">{pick(s, "name")}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="text-sm font-extrabold mb-2">{t("dash.badges")}</p>
          <div className="grid grid-cols-3 gap-2">
            {badges.map((b) => (
              <button key={b.key} data-testid={`badge-option-${b.key}`} onClick={() => setBadgeKey(b.key)}
                className={`rounded-xl p-3 border flex flex-col items-center gap-1.5 transition-all ${badgeKey === b.key ? "scale-[1.04]" : "opacity-75 hover:opacity-100"}`}
                style={{
                  background: `linear-gradient(165deg, ${b.color}${badgeKey === b.key ? "3D" : "14"}, ${b.color}06)`,
                  borderColor: `${b.color}${badgeKey === b.key ? "CC" : "33"}`,
                  boxShadow: badgeKey === b.key ? `0 0 14px ${b.color}55` : "none",
                }}>
                <Icon name={b.icon} className="w-6 h-6" style={{ color: b.color }} />
                <span className="text-[10px] font-extrabold leading-tight text-center">{pick(b, "name")}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-extrabold mb-1.5">{t("teacher.reason")} <span className="text-[#A5A0D6] font-semibold">({t("common.optional")})</span></p>
          <input data-testid="badge-reason-input" className="input-magic" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <button data-testid="badge-confirm-btn" disabled={!studentId || !badgeKey || busy} onClick={submit} className="btn-magic w-full">
          {busy ? t("common.loading") : t("common.confirm")}
        </button>
      </div>
    </Modal>
  );
}
